import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (!gsap.core.globals().ScrollTrigger) {
  gsap.registerPlugin(ScrollTrigger);
}

/**
 * Interactive Health Hero (merged topper + intro)
 */
class InteractiveHealthIntro {
  constructor(el) {
    this.introEl = el;
    if (!this.introEl) {
      return;
    }

    this.topper = this.introEl.querySelector(
      '.interactive-health-intro__topper',
    );
    this.subheadingWrapper = this.introEl.querySelector(
      '.interactive-health-intro__subheading-wrapper',
    );

    this.subheading = this.introEl.querySelector(
      '.interactive-health-intro__subheading',
    );
    this.body = this.introEl.querySelector('.interactive-health-intro__body');

    // Nothing to animate without at least the subheading or the body.
    if (!this.subheadingWrapper && !this.body) {
      return;
    }

    this.init();
  }

  getHeaderOffset() {
    const mainHeader = document.querySelector('.main-header');
    return mainHeader ? mainHeader.offsetHeight : 70;
  }

  init() {
    const headerOffset = this.getHeaderOffset();
    const media = gsap.matchMedia();

    media.add('(prefers-reduced-motion: no-preference)', () => {
      this.introEl.classList.add('is-animated');

      let scene = null;

      const build = () => {
        if (scene) {
          if (scene.scrollTrigger) {
            scene.scrollTrigger.kill();
          }
          scene.kill();
        }

        /*
        Pin the stage height to the REAL header height (the pin starts at
        `top+=headerOffset`).
        */
        this.introEl.style.setProperty('--ihi-nav-offset', `${headerOffset}px`);

        const availH = Math.max(1, window.innerHeight - headerOffset);
        const subH = this.subheading ? this.subheading.offsetHeight : 0;
        const bodyH = this.body ? this.body.offsetHeight : 0;
        const gap = Math.max(48, availH * 0.06);

        const isGeneral = document.body.classList.contains(
          'climate-health-general-template',
        );

        /*
        Phase C pulls a tall body flush to the bottom of the pinned stage.
        */
        let bodyRest = subH / 2 + gap;
        const exitDelta = Math.max(0, bodyRest + bodyH - availH / 2);

        /*
        General template: rest short bodies flush at the stage bottom; tall
        bodies already end flush via Phase C.
        */
        if (isGeneral && exitDelta === 0) {
          bodyRest = availH / 2 - bodyH;
        }

        const bodyStart = bodyRest + availH * 0.6;

        /*
        Per-phase scroll lengths (1:1 with the timeline so motion feels natural).
        */
        const crossfadeScroll = availH * 0.7;
        const floatScroll = bodyStart - bodyRest;
        const exitScroll = exitDelta;
        const total = crossfadeScroll + floatScroll + exitScroll;

        if (this.topper) {
          gsap.set(this.topper, { autoAlpha: 1 });
        }
        if (this.subheadingWrapper) {
          gsap.set(this.subheadingWrapper, { autoAlpha: 0, y: 0 });
        }
        if (this.body) {
          gsap.set(this.body, { autoAlpha: 0, y: bodyStart });
        }

        scene = gsap.timeline({
          scrollTrigger: {
            trigger: this.introEl,
            start: `top top+=${headerOffset}`,
            end: `+=${total}`,
            pin: this.introEl,
            pinSpacing: true,
            scrub: true,
            /*
            General template: Keep `overflow: hidden` while the hero is pinned 
            and animating, then lift it once the pin releases so the downward 
            flyout isn't cropped.
            */
            onLeave: () => {
              if (isGeneral) {
                this.introEl.style.overflow = 'visible';
              }
            },
            onEnterBack: () => {
              if (isGeneral) {
                this.introEl.style.overflow = 'hidden';
              }
            },
            onRefresh: self => {
              if (isGeneral) {
                this.introEl.style.overflow = self.isActive
                  ? 'hidden'
                  : 'visible';
              }
            },
          },
        });

        // Phase A — crossfade: topper out / subheading in.
        if (this.topper) {
          scene.to(
            this.topper,
            { autoAlpha: 0, ease: 'none', duration: crossfadeScroll },
            0,
          );
        }
        if (this.subheadingWrapper) {
          scene.to(
            this.subheadingWrapper,
            { autoAlpha: 1, ease: 'none', duration: crossfadeScroll },
            0,
          );
        }

        /*
        Phase B — body floats up from the bottom and fades in. As it
        enters the viewport the subheading fades out IN PLACE.
        */
        if (this.body) {
          scene.to(
            this.body,
            { autoAlpha: 1, ease: 'none', duration: floatScroll * 0.45 },
            crossfadeScroll,
          );
          scene.to(
            this.body,
            { y: bodyRest, ease: 'none', duration: floatScroll },
            crossfadeScroll,
          );
        }
        if (this.subheadingWrapper) {
          // Begin the fade once the body's top edge has entered the viewport.
          const enterFrac = gsap.utils.clamp(
            0,
            1,
            (bodyStart - availH / 2) / Math.max(1, floatScroll),
          );
          scene.to(
            this.subheadingWrapper,
            {
              autoAlpha: 0,
              ease: 'none',
              duration: floatScroll * (1 - enterFrac),
            },
            crossfadeScroll + floatScroll * enterFrac,
          );
        }

        /*
        Phase C — the subheading is gone; the body keeps rising until its
        end has scrolled through.
        */
        const exitAt = crossfadeScroll + floatScroll;
        if (this.body) {
          scene.to(
            this.body,
            { y: bodyRest - exitDelta, ease: 'none', duration: exitScroll },
            exitAt,
          );
        }
      };

      build();

      /*
      The body / subheading heights depend on the web font, so rebuild
      once fonts are ready and after full load with correct measurements.
      */
      const rebuild = () => {
        build();
        ScrollTrigger.refresh();
      };
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(rebuild);
      }
      window.addEventListener('load', rebuild);

      return () => {
        window.removeEventListener('load', rebuild);
        if (scene) {
          if (scene.scrollTrigger) {
            scene.scrollTrigger.kill();
          }
          scene.kill();
        }
        this.introEl.classList.remove('is-animated');
        this.introEl.style.overflow = '';
        gsap.set(
          [this.topper, this.subheadingWrapper, this.body].filter(Boolean),
          { clearProps: 'all' },
        );
      };
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.interactive-health-intro').forEach(el => {
    new InteractiveHealthIntro(el);
  });
});
